import { isPlatformWeb } from 'renative';
import { isMobile, isTablet } from 'react-device-detect';
import { BASE_GAP } from '@24i/nxg-core-utils/src/constants';
import { log } from '@24i/nxg-core-utils/src/logger';

export const isEmptyString = (s) => typeof s !== 'string' || s.length === 0;
export const isInCorrectBucket = (width, currentBucket, nextBucket) => {
    if (!nextBucket) {
        return true;
    }

    const maxWidth = currentBucket.value?.maxWidth;
    const minWidth = currentBucket.value?.minWidth;
    const nextMinWidth = nextBucket.value?.minWidth;

    let isCorrect;
    if (maxWidth && minWidth) {
        isCorrect = width <= maxWidth && width >= minWidth;
    } else if (maxWidth) {
        isCorrect = width <= maxWidth;
    } else if (minWidth) {
        isCorrect = width >= minWidth && (nextMinWidth ? width < nextMinWidth : true);
    } else {
        throw new Error('missing either "minWidth" or "maxWidth"');
    }

    return isCorrect;
};

export const transformToArray = (sizeBucketsObject) =>
    Object.keys(sizeBucketsObject).map((key) => ({
        key,
        value: sizeBucketsObject[key],
    }));

export const sortSizeBuckets = (sizesArray) => {
    // order sizes by smallest width
    sizesArray.sort((a, b) => {
        const sizeA = a.value?.maxWidth || a.value?.minWidth;
        const sizeB = b.value?.maxWidth || b.value?.minWidth;

        return sizeA - sizeB;
    });

    return sizesArray;
};

// Function to detect if web environment is running on a webview on iOS or Android
const webViewrules = [
    // if it says it's a webview, let's go with that
    'WebView',
    // iOS webview will be the same as safari but missing "Safari"
    '(iPhone|iPod|iPad|Macintosh)(?!.*Safari)',
    // Android Lollipop and Above: webview will be the same as native but it will contain "wv"
    // Android KitKat to Lollipop webview will put Version/X.X Chrome/{version}.0.0.0
    'Android.*(;\\s+wv|Version/\\d.\\d\\s+Chrome/\\d+(\\.0){3})',
    // Android
    'Telegram-Android',
    // old chrome android webview agent
    'Linux; U; Android',
];

const webDesktopRules = [
    // Telegram Desktop Macos
    'macos',
    // Telegram Desktop webA
    'weba',
    // Telegram Desktop webB
    'webb',
    // Telegram Desktop tdesktop
    'tdesktop',
];

const webDesktopAppRules = [
    // Telegram Desktop Macos
    'macos',
    // Telegram Desktop tdesktop
    'tdesktop',
];

const webviewRegExp = new RegExp(`(${webViewrules.join('|')})`, 'ig');
const webDesktopRegExp = new RegExp(`(${webDesktopRules.join('|')})`, 'ig');
const webDesktopAppRegExp = new RegExp(`(${webDesktopAppRules.join('|')})`, 'ig');

export const isWebView = () => {
    if (!isPlatformWeb) return false;

    const { userAgent } = navigator;

    return Boolean(userAgent.match(webviewRegExp));
};

export const isAndroidWeb = () => {
    if (!isPlatformWeb) {
        return false;
    }

    const { userAgent } = navigator;
    return Boolean(userAgent.match('Android'));
};

export const getTelegramWebApp = () => {
    const initParams = window.Telegram?.WebView?.initParams;
    const webApp =
        initParams?.tgWebAppVersion && window.Telegram?.WebApp ? window.Telegram?.WebApp : null;

    return webApp;
};

export const isTelegramKVersion = () => {
    const TelegramWebApp = getTelegramWebApp();

    // Early exit if no TelegramWebApp
    if (!TelegramWebApp) {
        return false;
    }

    // First check: platform property has K identifier
    if (TelegramWebApp.platform && typeof TelegramWebApp.platform === 'string') {
        // Check for K identifiers in platform string
        if (/(_k$|^k$|_k_|^webk$)/.test(TelegramWebApp.platform.toLowerCase())) {
            return true;
        }

        // Common case: 'web' platform with isIframe true indicates Telegram K
        if (
            TelegramWebApp.platform.toLowerCase() === 'web' &&
            window.Telegram?.WebView?.isIframe === true
        ) {
            return true;
        }
    }

    // Second check: User agent contains K identifier
    if (
        navigator?.userAgent &&
        typeof navigator.userAgent === 'string' &&
        navigator.userAgent.includes('KWebKit')
    ) {
        return true;
    }

    // Third check: explicit isK property
    if (window.Telegram?.WebView?.isK === true) {
        return true;
    }

    // Fourth check: WebView presence with K-specific properties
    if (window.Telegram?.WebView) {
        // K version often has iframe context with initParams
        if (window.Telegram.WebView.isIframe === true && window.Telegram.WebView.initParams) {
            return true;
        }
    }

    return false;
};

export const isTelegramAVersion = () => {
    const TelegramWebApp = getTelegramWebApp();

    // Early exit if no TelegramWebApp
    if (!TelegramWebApp) {
        return false;
    }

    // First check: platform property has A identifier
    if (TelegramWebApp.platform && typeof TelegramWebApp.platform === 'string') {
        // Check for A identifiers in platform string
        if (/(_a$|^a$|_a_|^weba$)/.test(TelegramWebApp.platform.toLowerCase())) {
            return true;
        }
    }

    // Second check: User agent contains A identifier
    if (navigator?.userAgent && typeof navigator.userAgent === 'string') {
        if (
            navigator.userAgent.includes('TelegramWebApp') ||
            navigator.userAgent.includes('TelegramBot')
        ) {
            // If we have TelegramWebApp in user agent but it's not K, it's likely A
            if (!isTelegramKVersion()) {
                return true;
            }
        }
    }

    // Third check: Has WebApp but is not K and specific A-version properties
    return Boolean(
        TelegramWebApp &&
            !isTelegramKVersion() &&
            window.Telegram?.WebView?.isIframe !== true &&
            (TelegramWebApp.version || TelegramWebApp.viewportHeight)
    );
};

export const isDesktopWebView = () => {
    if (!isPlatformWeb) return false;

    const { platform } = getTelegramWebApp() || {};
    return Boolean(platform && platform.match(webDesktopRegExp));
};

export const isDesktopApp = () => {
    if (!isPlatformWeb) return false;

    const { platform } = getTelegramWebApp() || {};
    return Boolean(platform && platform.match(webDesktopAppRegExp));
};

export const getTelegramWebAppSafeAreaInsets = () => {
    const TelegramWebApp = getTelegramWebApp();
    return TelegramWebApp?.safeAreaInset;
};

export const getTelegramWebAppBottomSafeAreaInset = () => {
    const insets = getTelegramWebAppSafeAreaInsets();
    return insets?.bottom ?? 0;
};

export const getTelegramWebAppTopSafeAreaInset = () => {
    const insets = getTelegramWebAppSafeAreaInsets();
    return insets?.top ?? 0;
};

export const getTelegramWebAppInsets = () => {
    const TelegramWebApp = getTelegramWebApp();
    return TelegramWebApp?.contentSafeAreaInset;
};

export const getTelegramWebAppTopInset = () => {
    const insets = getTelegramWebAppInsets();
    return insets?.top ?? 0;
};

export const getTelegramWebAppViewportHeight = () => {
    const TelegramWebApp = getTelegramWebApp();
    return TelegramWebApp?.viewportHeight;
};

export const isWebMobile = () =>
    // Tablet also tests true for mobile
    isMobile && !isTablet;

export const calculateTelegramGap = (baseGapRepetitions = 1) => {
    const telegramTopInset = getTelegramWebAppTopInset();
    const TelegramWebApp = getTelegramWebApp();
    const isFullscreen = TelegramWebApp?.isFullscreen === true;

    if (isDesktopApp()) {
        // For desktop apps, don't know why (maybe bug) but top inset value that comes from Telegram desktop application is too big
        // need to divide it by 2
        return isFullscreen ? telegramTopInset / 2 : 0;
    }

    // For Telegram A, handle fullscreen and non-fullscreen differently
    if (isTelegramAVersion()) {
        // CRITICAL: For non-fullscreen in Telegram A, ALWAYS return 0 (fixes spacing issues)
        // This applies to ALL screen widths, especially small widths with bottom tab bar
        if (!isFullscreen) {
            log('[calculateTelegramGap] Telegram A non-fullscreen - using zero gap');
            return 0;
        }
        // For fullscreen in Telegram A, return the inset
        return telegramTopInset;
    }

    if (telegramTopInset < 1 || isDesktopWebView()) return 0;

    return BASE_GAP * baseGapRepetitions + telegramTopInset;
};

export const expandTelegramApp = () => {
    const TelegramApp = getTelegramWebApp();

    if (TelegramApp) {
        TelegramApp.expand();
    }
};

export const getTelegramWebAppTopFullScreen = () => {
    const isFullScreen = getTelegramWebApp()?.isFullscreen;
    return isFullScreen ? getTelegramWebAppTopInset() : BASE_GAP;
};

/**
 * Store for tracking the last back button click time
 * Using a module-level variable prevents window pollution while still providing debouncing
 */
const telegramBackButtonStore = {
    lastClickTime: 0,
    debounceInterval: 800, // ms
};

/**
 * Creates a debounced back button handler to prevent double triggering
 * @param {Function} onBack - Original back button handler
 * @returns {() => void} Debounced handler
 */
const createDebouncedBackHandler = (onBack) => () => {
    const now = Date.now();
    const timeSinceLastClick = now - telegramBackButtonStore.lastClickTime;

    // Prevent clicks that happen too quickly (within debounce interval)
    if (timeSinceLastClick < telegramBackButtonStore.debounceInterval) {
        log('[Telegram] Preventing double-trigger:', {
            timeSinceLastClick,
            now,
            lastClick: telegramBackButtonStore.lastClickTime,
        });
        return;
    }

    // Update the last click time
    telegramBackButtonStore.lastClickTime = now;

    // Execute the back action
    log('[Telegram] Executing back action');
    onBack();
};

/**
 * Safely removes onClick handler from Telegram back button
 * @param {Object} backButton - Telegram BackButton object
 */
const removeBackButtonHandler = (backButton) => {
    if (typeof backButton.offClick === 'function') {
        try {
            backButton.offClick();
        } catch (e) {
            log('Error in offClick:', e);
        }
    }
};

/**
 * Sets Telegram K version back button visibility
 * @param {boolean} isVisible - Whether to show (true) or hide (false) the button
 */
/**
 * Try to set visibility using WebApp.BackButton method
 * @param {boolean} isVisible - Whether to show or hide
 * @returns {boolean} - Whether the method succeeded
 */
const tryWebAppBackButtonMethod = (isVisible) => {
    const method = isVisible ? 'show' : 'hide';
    if (!window.Telegram?.WebApp?.BackButton) {
        return false;
    }

    if (typeof window.Telegram.WebApp.BackButton[method] === 'function') {
        try {
            window.Telegram.WebApp.BackButton[method]();
            log(`Used standard ${method}() for K version`);
            return true;
        } catch (e) {
            log(`Error using ${method}() for K version:`, e);
        }
    }

    return false;
};

/**
 * Set visibility using WebApp.BackButton isVisible property
 * @param {boolean} isVisible - Whether to show or hide
 */
const setWebAppBackButtonProperty = (isVisible) => {
    if (!window.Telegram?.WebApp?.BackButton) {
        return;
    }

    if ('isVisible' in window.Telegram.WebApp.BackButton) {
        log(`WARNING: Falling back to isVisible property - this is not preferred`);
        window.Telegram.WebApp.BackButton.isVisible = isVisible;
    }
};

/**
 * Try to set visibility using older WebView methods
 * @param {boolean} isVisible - Whether to show or hide
 */
const tryWebViewMethods = (isVisible) => {
    const method = isVisible ? 'show' : 'hide';
    if (!window.Telegram?.WebView) {
        return;
    }

    try {
        // First try to use WebView.BackButton.show/hide if available
        if (
            window.Telegram.WebView.BackButton &&
            typeof window.Telegram.WebView.BackButton[method] === 'function'
        ) {
            window.Telegram.WebView.BackButton[method]();
            log(`Used WebView.BackButton.${method}() for K version`);
        } else {
            // Fallback to setting properties directly
            window.Telegram.WebView.BackButton = { isVisible };
        }

        // Always try setParams if available as an additional method
        if (typeof window.Telegram.WebView.setParams === 'function') {
            window.Telegram.WebView.setParams({
                back_button_visible: isVisible,
            });
        }
    } catch (e) {
        log(`Error setting WebView.BackButton visibility to ${isVisible}:`, e);
    }
};

/**
 * Try to use K-specific API
 * @param {boolean} isVisible - Whether to show or hide
 */
const tryKSpecificAPI = (isVisible) => {
    if (
        !window.K ||
        typeof window.K[isVisible ? 'showBackButton' : 'hideBackButton'] !== 'function'
    ) {
        return;
    }

    try {
        window.K[isVisible ? 'showBackButton' : 'hideBackButton']();
    } catch (e) {
        log(`Error ${isVisible ? 'showing' : 'hiding'} K back button:`, e);
    }
};

/**
 * Sets Telegram K version back button visibility
 * @param {boolean} isVisible - Whether to show (true) or hide (false) the button
 */
const setKVersionButtonVisibility = (isVisible) => {
    try {
        // Try standard WebApp.BackButton method first
        const methodSucceeded = tryWebAppBackButtonMethod(isVisible);

        // Fall back to other methods if needed
        if (!methodSucceeded) {
            setWebAppBackButtonProperty(isVisible);
            tryWebViewMethods(isVisible);
            tryKSpecificAPI(isVisible);
        }
    } catch (e) {
        log(`Error setting K back button visibility to ${isVisible}:`, e);
    }
};

/**
 * Shows or hides the back button with standard method
 * @param {Object} backButton - Telegram BackButton object
 * @param {boolean} show - Whether to show or hide
 */
const setBackButtonVisibility = (backButton, show) => {
    const method = show ? 'show' : 'hide';

    if (typeof backButton[method] !== 'function') {
        return;
    }

    try {
        backButton[method]();
    } catch (e) {
        log(`Error in ${method}():`, e);
    }
};

/**
 * Creates a cleanup function for back button
 * @param {Object} TelegramWebApp - Telegram WebApp object
 * @param {boolean} isTelegramK - Whether this is Telegram K version
 * @returns {() => void} Cleanup function
 */
const createBackButtonCleanup = (TelegramWebApp, isTelegramK) => {
    // Return the cleanup function
    return () => {
        if (!TelegramWebApp?.BackButton) {
            return;
        }

        // Remove the onClick handler
        removeBackButtonHandler(TelegramWebApp.BackButton);

        // Hide the button using appropriate methods
        if (isTelegramK) {
            setKVersionButtonVisibility(false);
        }

        setBackButtonVisibility(TelegramWebApp.BackButton, false);
    };
};

/**
 * Initialize the Telegram back button
 * This function consolidates all back button handling logic for both Telegram K and A versions
 * @param {Object} TelegramWebApp - The Telegram WebApp instance
 * @param {Function} onBack - The callback to execute when back button is clicked
 * @param {boolean} isTelegramK - Whether this is Telegram K version
 * @param {boolean} forceShow - Whether to force showing the native back button
 * @returns {(() => void)|null} Cleanup function or null if initialization failed
 */
/**
 * Show back button with additional attempts for Telegram A
 */
const showTelegramABackButton = () => {
    if (window.Telegram?.WebApp?.enableBackButton) {
        try {
            window.Telegram.WebApp.enableBackButton();
        } catch (e) {
            // Ignore errors
        }
    }
};

/**
 * Apply button visibility with delay to ensure it's properly applied
 */
const applyBackButtonVisibilityWithDelay = (TelegramWebApp, isTelegramK, isTelegramA, show) => {
    setTimeout(() => {
        setBackButtonVisibility(TelegramWebApp.BackButton, show);

        if (isTelegramK) {
            setKVersionButtonVisibility(show);
        }

        // For Telegram A showing, use special methods
        if (show && isTelegramA) {
            showTelegramABackButton();
        }

        // Final attempt for showing after a longer delay
        if (show) {
            setTimeout(() => {
                setBackButtonVisibility(TelegramWebApp.BackButton, show);
            }, 300);
        }
    }, 100);
};

/**
 * Set up back button click handler
 */
const setupBackButtonClickHandler = (TelegramWebApp, onBack) => {
    // Create a strongly debounced version of onBack
    const debouncedOnBack = createDebouncedBackHandler(onBack);

    // Remove existing handlers to prevent double-triggering
    removeBackButtonHandler(TelegramWebApp.BackButton);

    // Set up the onClick handler
    try {
        TelegramWebApp.BackButton.onClick(debouncedOnBack);
    } catch (e) {
        log('Error in onClick:', e);
    }
};

/**
 * Handle back button visibility
 */
const configureBackButtonVisibility = (TelegramWebApp, isTelegramK, isTelegramA, forceShow) => {
    if (forceShow) {
        log('[initTelegramBackButton] Showing native Telegram button');

        // Use version-specific approaches
        if (isTelegramK) {
            setKVersionButtonVisibility(true);
        }

        // Common method for both versions
        setBackButtonVisibility(TelegramWebApp.BackButton, true);

        // Make additional attempts to ensure visibility
        applyBackButtonVisibilityWithDelay(TelegramWebApp, isTelegramK, isTelegramA, true);
    } else {
        // Hide button case - rarely used
        log('[initTelegramBackButton] Hiding native button (uncommon case)');

        // Use version-specific approaches
        if (isTelegramK) {
            setKVersionButtonVisibility(false);
        }

        // Common method for both versions
        setBackButtonVisibility(TelegramWebApp.BackButton, false);

        // Ensure it stays hidden with a delay
        applyBackButtonVisibilityWithDelay(TelegramWebApp, isTelegramK, isTelegramA, false);
    }

    // Return cleanup function
    return createBackButtonCleanup(TelegramWebApp, isTelegramK);
};

export const initializeTelegramBackButton = (
    TelegramWebApp,
    onBack,
    isTelegramK,
    forceShow = true
) => {
    // Early exit if requirements aren't met
    if (!TelegramWebApp || !TelegramWebApp.BackButton || typeof onBack !== 'function') {
        return null;
    }

    // Get Telegram version info
    const isTelegramA = isTelegramAVersion();

    log('[initTelegramBackButton] Initializing back button:', {
        isTelegramK,
        isTelegramA,
        forceShow,
        platform: TelegramWebApp.platform,
    });

    // Set up click handler
    setupBackButtonClickHandler(TelegramWebApp, onBack);

    // Configure visibility and return cleanup
    return configureBackButtonVisibility(TelegramWebApp, isTelegramK, isTelegramA, forceShow);
};
